import React, { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import { FIELD, LABEL } from "../forms/calfFields";
import { SectionCard } from "./SectionCard";

const priceLabel = (feed) =>
  feed.price_dt != null ? `${feed.price_dt} fr / dt` : "Prix non renseigné";

/**
 * The aliments a calf receives.
 *
 * Feeds live in the user's own catalogue, so the same aliment is described once
 * and then attached to whichever calves get it. This offers both: pick something
 * already catalogued, or describe a new one — which catalogues it and attaches it
 * in one go, because being made to do those as two steps would be tedious.
 */
function CalfFeeds({ calf, canEdit = true }) {
  const calfId = calf.pk_calf;
  const [attached, setAttached] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [isAdding, setAdding] = useState(false);
  const [error, setError] = useState(null);

  const [picked, setPicked] = useState("");
  const [newName, setNewName] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [isSaving, setSaving] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.get("/feed/calf/" + calfId), api.get("/feed")])
      .then(([calfFeeds, all]) => {
        setAttached(calfFeeds.data);
        setCatalogue(all.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("There was an error getting the feeds!", err);
        setLoading(false);
      });
  }, [calfId]);

  useEffect(() => {
    load();
  }, [load]);

  const closeForm = () => {
    setAdding(false);
    setPicked("");
    setNewName("");
    setNewPrice("");
    setError(null);
  };

  const attach = (feedId) =>
    api.post("/feed/calf/" + calfId, { fk_feed: feedId }).then(() => {
      closeForm();
      load();
    });

  const handleAdd = (event) => {
    event.preventDefault();
    setError(null);

    // Picking from the catalogue and describing a new one are the same action
    // from the user's side, so one button handles whichever they filled in.
    if (picked) {
      setSaving(true);
      attach(Number(picked))
        .catch((err) => {
          console.error("There was an error attaching the feed!", err);
          setError("L'ajout a échoué. Réessayez.");
        })
        .finally(() => setSaving(false));
      return;
    }

    if (!newName.trim()) {
      setError("Choisissez un aliment ou donnez un nom au nouveau.");
      return;
    }

    setSaving(true);
    api
      .post("/feed", {
        name: newName.trim(),
        price_dt:
          newPrice === "" ? null : parseFloat(String(newPrice).replace(",", ".")),
      })
      .then((response) => attach(response.data.pk_feed))
      .catch((err) => {
        console.error("There was an error creating the feed!", err);
        setError("L'ajout a échoué. Réessayez.");
      })
      .finally(() => setSaving(false));
  };

  const handleRemove = (feedId) => {
    api
      .delete("/feed/calf/" + calfId + "/" + feedId)
      .then(load)
      .catch((err) => {
        console.error("There was an error removing the feed!", err);
      });
  };

  if (isLoading) {
    return null;
  }

  const attachedIds = new Set(attached.map((f) => f.pk_feed));
  const available = catalogue.filter((f) => !attachedIds.has(f.pk_feed));

  return (
    <>
      <SectionCard
        title="Aliments"
        action={
          canEdit && !isAdding ? (
            <button
              onClick={() => setAdding(true)}
              className="text-[15px] font-medium text-amber-500 dark:text-amber-400"
            >
              Ajouter
            </button>
          ) : null
        }
      >
        {attached.length === 0 ? (
          <p className="px-4 py-4 text-sm text-stone-400 dark:text-slate-400">
            Aucun aliment
          </p>
        ) : (
          attached.map((feed, index) => (
            <div
              key={feed.pk_feed}
              className={
                "flex min-h-[44px] items-center justify-between px-4" +
                (index === attached.length - 1
                  ? ""
                  : " border-b border-stone-200/80 dark:border-slate-700/70")
              }
            >
              <div className="flex flex-col py-2">
                <span className="text-[15px] text-stone-900 dark:text-white">
                  {feed.name}
                </span>
                <span className="text-sm text-stone-400 dark:text-slate-400">
                  {priceLabel(feed)}
                </span>
              </div>
              {canEdit && (
                <button
                  onClick={() => handleRemove(feed.pk_feed)}
                  aria-label={"Retirer " + feed.name}
                  className="px-2 text-[15px] font-medium text-stone-400 hover:text-red-500 dark:text-slate-400"
                >
                  Retirer
                </button>
              )}
            </div>
          ))
        )}
      </SectionCard>

      {isAdding && (
        <form
          onSubmit={handleAdd}
          className="mt-3 flex flex-col rounded-xl bg-white p-4 md:max-w-xl md:bg-transparent md:p-0 dark:bg-slate-800 dark:md:bg-transparent"
        >
          {available.length > 0 && (
            <>
              <label htmlFor="feed_pick_input" className={LABEL}>
                Depuis vos aliments
              </label>
              <select
                className={FIELD}
                value={picked}
                onChange={(e) => setPicked(e.target.value)}
                name="feed_pick_input"
              >
                <option value="">Choisir un aliment</option>
                {available.map((feed) => (
                  <option key={feed.pk_feed} value={feed.pk_feed}>
                    {feed.name} — {priceLabel(feed)}
                  </option>
                ))}
              </select>
            </>
          )}

          {!picked && (
            <>
              <label htmlFor="feed_name_input" className={LABEL}>
                {available.length > 0 ? "Ou un nouvel aliment" : "Nouvel aliment"}
              </label>
              <input
                className={FIELD}
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Nom de l'aliment"
                name="feed_name_input"
              />

              <label htmlFor="feed_price_input" className={LABEL}>
                Prix (francs / dt)
              </label>
              <input
                className={FIELD}
                type="text"
                inputMode="decimal"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                placeholder="Prix"
                name="feed_price_input"
              />
            </>
          )}

          {error && (
            <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={closeForm}
              className="w-1/2 rounded-lg border border-stone-300 bg-white py-3 font-medium text-stone-600 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="w-1/2 rounded-lg bg-amber-400 py-3 font-medium text-white disabled:opacity-50 dark:bg-amber-600"
            >
              {isSaving ? "Ajout…" : "Ajouter"}
            </button>
          </div>
        </form>
      )}
    </>
  );
}

export default CalfFeeds;
